import React, { useEffect, useRef, useState } from 'react';

interface DiskDefragProps {
  onActivity: () => void;
}

interface DiskSector {
  id: number;
  type: 'free' | 'used' | 'fragmented' | 'system' | 'bad';
  fileId?: number;
  scanning?: boolean;
  moving?: boolean;
  analyzed?: boolean;
}

export const DiskDefrag: React.FC<DiskDefragProps> = ({ onActivity }) => {
  const [sectors, setSectors] = useState<DiskSector[]>([]);
  const [currentSector, setCurrentSector] = useState(0);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<'analysis' | 'defrag' | 'complete'>('analysis');
  const animationRef = useRef<number>(0);
  const lastUpdateRef = useRef<number>(0);

  const GRID_SIZE = 80; // Same as desktop fullscreen mode - 80x80 grid
  const TOTAL_SECTORS = GRID_SIZE * GRID_SIZE;

  // Initialize disk with random data and restore previous scan state
  const initializeDisk = () => {
    const storageKey = 'diskDefragState';
    const savedState = localStorage.getItem(storageKey);
    const userSeed = localStorage.getItem('diskDefragSeed') || Math.random().toString();
    localStorage.setItem('diskDefragSeed', userSeed);
    
    // Use seed for consistent randomization per user
    const seededRandom = (seed: string, index: number) => {
      const hash = (seed + index.toString()).split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
      return Math.abs(hash) / 2147483647;
    };

    const newSectors: DiskSector[] = [];
    let fileId = 1;
    let analyzedSectors: Set<number> = new Set();
    let lastScanTime = 0;

    // Load previous state if exists
    if (savedState) {
      const parsed = JSON.parse(savedState);
      analyzedSectors = new Set(parsed.analyzedSectors || []);
      lastScanTime = parsed.lastScanTime || 0;
    }

    // Calculate degradation over time (gradual increase in fragmented sectors)
    const timeSinceLastScan = Date.now() - lastScanTime;
    const hoursSinceLastScan = timeSinceLastScan / (1000 * 60 * 60);
    const degradationRate = Math.min(hoursSinceLastScan * 0.1, 0.5); // Max 50% degradation
    
    for (let i = 0; i < TOTAL_SECTORS; i++) {
      const rand = seededRandom(userSeed, i);
      const degradationRand = seededRandom(userSeed + 'degrade', i);
      let sector: DiskSector;
      
      if (rand < 0.05) {
        // 5% system files
        sector = { id: i, type: 'system' };
      } else if (rand < 0.08) {
        // 3% bad sectors
        sector = { id: i, type: 'bad' };
      } else if (rand < 0.4) {
        // 32% free space
        sector = { id: i, type: 'free' };
      } else if (rand < 0.7 - degradationRate) {
        // Used sectors (reduced by degradation)
        sector = { id: i, type: 'used', fileId: fileId++ };
      } else {
        // Fragmented (increased by degradation and time)
        sector = { id: i, type: 'fragmented', fileId: Math.floor(seededRandom(userSeed + 'file', i) * 50) + 1 };
      }

      // Apply previous scan state but degrade some analyzed sectors over time
      if (analyzedSectors.has(i) && degradationRand > degradationRate) {
        sector.analyzed = true;
      }
      
      newSectors.push(sector);
    }
    
    setSectors(newSectors);
  };

  // Animation loop
  const animate = (timestamp: number) => {
    if (timestamp - lastUpdateRef.current > 30) { // Update every 30ms for smoother animation
      if (phase === 'analysis') {
        // Analysis phase - scan sectors with desktop-like batch processing
        if (currentSector < TOTAL_SECTORS) {
          setSectors(prevSectors => {
            const newSectors = [...prevSectors];
            
            // Process in smaller increments but mark larger batches as "scanned"
            const INCREMENT = 40; // Smooth visual increment
            const BATCH_SIZE = 1600; // Desktop batch size for analysis marking
            
            // Mark sectors as analyzed in batches of 1600
            const currentBatch = Math.floor(currentSector / BATCH_SIZE) * BATCH_SIZE;
            const nextBatchStart = currentBatch + BATCH_SIZE;
            
            // Clear all scanning states first
            newSectors.forEach(s => s.scanning = false);
            
            // Mark completed batches as analyzed
            for (let i = 0; i < currentBatch; i++) {
              if (newSectors[i]) {
                newSectors[i].analyzed = true;
              }
            }
            
            // Show scanning effect for current sector and a small range around it
            const scanStart = currentSector;
            const scanEnd = Math.min(scanStart + INCREMENT, TOTAL_SECTORS);
            
            for (let i = scanStart; i < scanEnd; i++) {
              if (newSectors[i]) {
                newSectors[i].scanning = true;
              }
            }
            
            return newSectors;
          });
          setCurrentSector(prev => prev + 40);
          setProgress((currentSector / TOTAL_SECTORS) * 50); // 50% for analysis
        } else {
          // Analysis complete, start defragmentation
          setSectors(prevSectors => {
            const newSectors = [...prevSectors];
            newSectors.forEach(s => { s.scanning = false; s.analyzed = true; });
            return newSectors;
          });
          setPhase('defrag');
          setCurrentSector(0);
        }
      } else if (phase === 'defrag') {
        setSectors(prevSectors => {
          const newSectors = [...prevSectors];
          // Defragmentation phase - move fragmented sectors
          const fragmentedSectors = newSectors.filter(s => s.type === 'fragmented');
          const freeSectors = newSectors.filter(s => s.type === 'free');
          
          if (fragmentedSectors.length > 0 && freeSectors.length > 0) {
            const fragmentedIndex = newSectors.findIndex(s => s.type === 'fragmented');
            const freeIndex = newSectors.findIndex(s => s.type === 'free');
            
            if (fragmentedIndex !== -1 && freeIndex !== -1) {
              // Animate movement
              newSectors[fragmentedIndex].moving = true;
              newSectors[freeIndex].moving = true;
              
              // Use setTimeout for proper animation like desktop app
              setTimeout(() => {
                setSectors(prevSectors => {
                  const updatedSectors = [...prevSectors];
                  // Move fragmented sector to free space
                  updatedSectors[freeIndex] = {
                    ...updatedSectors[fragmentedIndex],
                    id: freeIndex,
                    type: 'used',
                    moving: false,
                    analyzed: true
                  };
                  updatedSectors[fragmentedIndex] = {
                    id: fragmentedIndex,
                    type: 'free',
                    moving: false,
                    analyzed: true
                  };
                  return updatedSectors;
                });
              }, 200);
            }
            
            const remainingFragmented = newSectors.filter(s => s.type === 'fragmented').length;
            const totalFragmented = TOTAL_SECTORS * 0.35; // Original fragmented percentage
            const defragProgress = 50 + ((1 - remainingFragmented / totalFragmented) * 50);
            setProgress(Math.min(defragProgress, 100));
          } else {
            // Defragmentation complete - save scan state and restart
            setPhase('complete');
            setProgress(100);
            
            // Save scan state to localStorage
            const analyzedSectorIds = newSectors
              .filter(s => s.analyzed)
              .map(s => s.id);
            
            localStorage.setItem('diskDefragState', JSON.stringify({
              analyzedSectors: analyzedSectorIds,
              lastScanTime: Date.now()
            }));
            
            // Restart after a brief pause
            setTimeout(() => {
              initializeDisk();
              setPhase('analysis');
              setCurrentSector(0);
              setProgress(0);
            }, 2000);
          }
          
          return newSectors;
        });
      }
      
      lastUpdateRef.current = timestamp;
    }
    
    animationRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    initializeDisk();
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const getSectorColor = (sector: DiskSector) => {
    if (sector.scanning) return 'bg-yellow-400 animate-pulse';
    if (sector.moving) return 'bg-blue-400 animate-bounce';
    if (sector.analyzed && sector.type === 'free') return 'bg-cyan-600'; // Analyzed free space
    if (sector.analyzed && sector.type === 'used') return 'bg-lime-500'; // Analyzed used space
    
    switch (sector.type) {
      case 'free': return 'bg-blue-900';
      case 'used': return 'bg-green-600';
      case 'fragmented': return 'bg-red-500';
      case 'system': return 'bg-purple-600';
      case 'bad': return 'bg-gray-800';
      default: return 'bg-gray-600';
    }
  };

  // Calculate stats like desktop version
  const freeSectors = sectors.filter(s => s.type === 'free').length;
  const usedSectors = sectors.filter(s => s.type === 'used').length;
  const fragmentedSectors = sectors.filter(s => s.type === 'fragmented').length;
  const fragmentedFiles = new Set(
    sectors.filter(s => s.type === 'fragmented').map(s => s.fileId)
  ).size;

  return (
    <div 
      className="fixed inset-0 bg-black cursor-none z-[9999] flex"
      onClick={onActivity}
      onMouseMove={onActivity}
      onKeyDown={onActivity}
    >
      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {/* Title */}
        <div className="text-white text-center mb-6">
          <h1 className="text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-green-400 bg-clip-text text-transparent">
            Disk Defragmenter
          </h1>
          <div className="text-2xl mb-2">
            <span className="text-gray-300">Disk C: Analysis</span> 
            <span className="text-blue-400 font-mono ml-2">({GRID_SIZE}x{GRID_SIZE} = {TOTAL_SECTORS.toLocaleString()} sectors)</span>
          </div>
          <div className="flex justify-center items-center gap-6 text-xl">
            <span className="text-gray-300">Status: <span className="font-mono text-green-400">
              {phase === 'analysis' && 'Analyzing...'}
              {phase === 'defrag' && 'Defragmenting...'}
              {phase === 'complete' && 'Complete'}
            </span></span>
            <span className="text-gray-300">Progress: <span className="font-mono text-cyan-400">{Math.round(progress)}%</span></span>
            {phase === 'analysis' && (
              <span className="text-gray-300">Sector: <span className="font-mono text-yellow-400">{currentSector}</span>/{TOTAL_SECTORS.toLocaleString()}</span>
            )}
          </div>
        </div>

        {/* Disk Visualization */}
        <div className="mb-6">
          <div 
            className="grid gap-[1px] p-8 bg-gray-900 rounded-lg border-2 border-gray-700 shadow-2xl"
            style={{ 
              gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)`,
              width: 'min(70vw, 70vh)',
              aspectRatio: '1'
            }}
          >
            {sectors.map((sector) => (
              <div
                key={sector.id}
                className={`aspect-square ${getSectorColor(sector)} transition-colors duration-200`}
                title={`Sector ${sector.id}: ${sector.type}${sector.fileId ? ` (File ${sector.fileId})` : ''}`}
              />
            ))}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="w-[600px] mb-4">
          <div className="w-full bg-gray-800 rounded-full h-6 border-2 border-gray-600 shadow-inner">
            <div 
              className="bg-gradient-to-r from-blue-500 via-cyan-400 to-green-500 h-full rounded-full transition-all duration-500 shadow-lg" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="text-center text-lg text-gray-300 mt-2 font-mono">
            {Math.round(progress)}% Complete
          </div>
        </div>

        {/* Instructions */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-gray-500 text-lg font-mono">
          Move mouse or press any key to exit
        </div>
      </div>

      {/* Sidebar Stats */}
      <div className="w-80 bg-gray-900 text-white p-6 space-y-6 border-l-2 border-gray-700">
        {/* Statistics */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-cyan-400 border-b border-gray-700 pb-2">Disk Statistics</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-300">Total sectors:</span>
              <span className="font-mono text-white">{TOTAL_SECTORS.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Used sectors:</span>
              <span className="font-mono text-green-400">{(usedSectors + fragmentedSectors).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Free sectors:</span>
              <span className="font-mono text-blue-400">{freeSectors.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Fragmented files:</span>
              <span className="font-mono text-red-400">{fragmentedFiles}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Fragmentation:</span>
              <span className="font-mono text-red-400">{Math.round((fragmentedSectors / TOTAL_SECTORS) * 100)}%</span>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-cyan-400 border-b border-gray-700 pb-2">Legend</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-blue-900 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>Free space</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-green-600 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>Used space</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-red-500 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>Fragmented files</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-purple-600 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>System files</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-gray-800 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>Bad sectors</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-cyan-600 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>Analyzed free</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-lime-500 border border-gray-600 rounded-sm shadow-sm"></div>
              <span>Analyzed used</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-yellow-400 border border-gray-600 rounded-sm shadow-sm animate-pulse"></div>
              <span>Scanning</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 bg-blue-400 border border-gray-600 rounded-sm shadow-sm animate-bounce"></div>
              <span>Moving</span>
            </div>
          </div>
        </div>

        {/* Phase Information */}
        <div className="space-y-3">
          <h3 className="text-xl font-bold text-cyan-400 border-b border-gray-700 pb-2">Current Phase</h3>
          <div className="text-sm space-y-2">
            <div className="text-gray-300">
              {phase === 'analysis' && (
                <>
                  <div className="text-yellow-400 font-semibold">Analysis Phase</div>
                  <div>Scanning sectors for fragmentation...</div>
                  <div className="mt-2 text-xs text-gray-400">
                    Sector {currentSector} of {TOTAL_SECTORS.toLocaleString()}
                  </div>
                </>
              )}
              {phase === 'defrag' && (
                <>
                  <div className="text-green-400 font-semibold">Defragmentation Phase</div>
                  <div>Moving fragmented files to free space...</div>
                </>
              )}
              {phase === 'complete' && (
                <>
                  <div className="text-cyan-400 font-semibold">Complete</div>
                  <div>Defragmentation finished successfully!</div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};